import { useRef, useEffect, useState, useCallback } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { XMarkIcon, PlusIcon, DocumentDuplicateIcon, XCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { ContextMenu, type ContextMenuItem } from './ContextMenu'
import { DragItemTypes, type EditorTabDragItem } from './dnd-types'


export interface ChromeTab {
  id: string
  label: string
  closeable?: boolean
  isDirty?: boolean
  content?: string
  savedQueryId?: string
  savedQueryCollectionId?: string
  savedQueryName?: string
  databaseName?: string
  schemaName?: string
}

interface ChromeTabsProps {
  tabs: ChromeTab[]
  activeTabId: string
  onTabChange: (tabId: string) => void
  onTabClose?: (tabId: string) => void
  onTabAdd?: () => void
  onConfirmClose?: (tabId: string) => Promise<boolean>
  onTabDuplicate?: (tabId: string) => void
  onCloseAllTabs?: () => void
  onCloseOtherTabs?: (tabId: string) => void
  onForceCloseAllTabs?: () => void
  className?: string
  maxTabWidth?: number
  enableDragDrop?: boolean
  datasourceId?: string
  databaseName?: string
  schemaName?: string
  tabHeight?: number
  containerHeightClass?: string
  reserveAddButtonSpace?: boolean
}

interface DraggableTabProps {
  tab: ChromeTab
  tabWidth: number
  isActive: boolean
  datasourceId?: string
  databaseName?: string
  schemaName?: string
  onTabChange: (tabId: string) => void
  onTabClose?: (tabId: string) => void
  onConfirmClose?: (tabId: string) => Promise<boolean>
  onContextMenu: (e: React.MouseEvent, tabId: string) => void
  onMiddleClick: (e: React.MouseEvent, tabId: string) => void
  enableDragDrop?: boolean
  tabHeight?: number
}

const DraggableTab: React.FC<DraggableTabProps> = ({
  tab,
  tabWidth,
  isActive,
  datasourceId,
  databaseName,
  schemaName,
  onTabChange,
  onTabClose,
  onConfirmClose,
  onContextMenu,
  onMiddleClick,
  enableDragDrop = false,
  tabHeight = 32
}) => {
  const dragRef = useRef<HTMLDivElement>(null)
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: DragItemTypes.EDITOR_TAB,
    item: (): EditorTabDragItem => ({
      type: DragItemTypes.EDITOR_TAB,
      id: tab.id,
      label: tab.label,
      content: tab.content || '',
      isDirty: tab.isDirty,
      savedQueryId: tab.savedQueryId,
      savedQueryCollectionId: tab.savedQueryCollectionId,
      savedQueryName: tab.savedQueryName,
      datasourceId: datasourceId,
      databaseName: tab.databaseName || databaseName,
      schemaName: tab.schemaName || schemaName
    }),
    canDrag: enableDragDrop,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_item, monitor) => {
      const dropResult = monitor.getDropResult()
      if (dropResult && typeof dropResult === 'object' && 'success' in dropResult && dropResult.success) {
        console.log('Tab dropped successfully:', dropResult)
      }
    }
  }), [tab, datasourceId, databaseName, schemaName, enableDragDrop])

  useEffect(() => {
    if (enableDragDrop) {
      preview(getEmptyImage(), { captureDraggingState: true });
      drag(dragRef);
    }
  }, [preview, enableDragDrop, drag])

  const handleCloseClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (tab.isDirty && onConfirmClose) {
      const confirmed = await onConfirmClose(tab.id)
      if (confirmed) {
        onTabClose?.(tab.id)
      }
    } else {
      onTabClose?.(tab.id)
    }
  }

  const handleCloseClickWrapper = (e: React.MouseEvent) => {
    void handleCloseClick(e)
  }

  const handleClick = () => {
    onTabChange(tab.id)
  }

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    onContextMenu(e, tab.id)
  }, [onContextMenu, tab.id])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 1) {
      onMiddleClick(e, tab.id)
    }
  }, [onMiddleClick, tab.id])

  // Determine what to show based on tab width
  const showText = tabWidth > 30
  const showCloseButton = tabWidth > 20
  const showDirtyIndicator = tab.isDirty && (tab.content || '').trim().length > 0

  return (
    <div
      ref={enableDragDrop ? dragRef : undefined}
      className={`
        relative flex items-center justify-center cursor-pointer select-none
        transition-all duration-200 ease-out group
        ${isActive 
          ? 'text-gray-900 dark:text-white bg-gray-200 dark:bg-gray-700 shadow-md' 
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
        }
        ${isDragging ? 'opacity-50' : ''}
        ${enableDragDrop ? 'cursor-grab active:cursor-grabbing' : ''}
      `}
      style={{ 
        width: `${tabWidth}px`,
        height: `${tabHeight}px`,
        minWidth: `${tabWidth}px`,
        maxWidth: `${tabWidth}px`,
        borderRadius: '6px',
        margin: '2px 1px',
        padding: tabWidth > 40 ? '0 8px' : tabWidth > 25 ? '0 4px' : '0 2px'
      }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      title={`${tab.label}${tab.isDirty ? ' (unsaved)' : ''}`}
    >
      {/* Text content */}
      {showText && (
        <div 
          className="flex-1 truncate text-sm font-medium min-w-0"
          style={{ 
            fontSize: tabWidth < 60 ? '11px' : '12px',
            lineHeight: '1.2'
          }}
        >
          {tab.label}
        </div>
      )}

      {/* Dirty indicator - show even when very small */}
      {showDirtyIndicator && (
        <div 
          className={`
            w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-500 flex-shrink-0 ring-1 ring-blue-400 dark:ring-blue-600
            ${showText ? 'ml-1' : 'absolute top-1 right-1'}
          `}
        />
      )}

      {/* Close button */}
      {showCloseButton && tab.closeable && onTabClose && (
        <button type="button"
          onClick={handleCloseClickWrapper}
          className={`
            flex items-center justify-center rounded-full
            opacity-0 group-hover:opacity-100 transition-opacity duration-200
            hover:bg-gray-200 dark:hover:bg-gray-500
            ${showText ? 'ml-1 w-4 h-4' : 'absolute top-1 right-1 w-3 h-3'}
            ${isActive ? 'opacity-60' : ''}
          `}
          title="Close tab"
        >
          <XMarkIcon className={showText ? 'w-3 h-3' : 'w-2 h-2'} />
        </button>
      )}
    </div>
  )
}

export function ChromeTabs({ 
  tabs, 
  activeTabId, 
  onTabChange, 
  onTabClose, 
  onTabAdd,
  onConfirmClose,
  onTabDuplicate,
  onCloseAllTabs,
  onCloseOtherTabs,
  onForceCloseAllTabs,
  className = '',
  maxTabWidth = 200,
  enableDragDrop = false,
  datasourceId,
  databaseName,
  schemaName,
  tabHeight = 32,
  containerHeightClass = 'h-10',
  reserveAddButtonSpace = false
}: ChromeTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const [tabWidth, setTabWidth] = useState(maxTabWidth)
  const [needsScrolling, setNeedsScrolling] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    tabId: string
    position: { x: number; y: number }
  } | null>(null)

  // Calculate tab width and scrolling needs
  const updateLayout = useCallback(() => {
    if (!containerRef.current || !tabsContainerRef.current) return
    
    const containerWidth = containerRef.current.offsetWidth
    const addButtonWidth = (reserveAddButtonSpace && !!onTabAdd) ? 40 : 0
    const scrollButtonWidth = 32 // Account for scroll buttons when visible
    const padding = 16
    const spacing = 8 // Extra spacing buffer
    
    // Calculate available width for tabs
    let availableWidth = containerWidth - addButtonWidth - padding - spacing
    
    // Reserve space for scroll buttons if we might need them
    if (tabs.length > 5) { // Preemptively reserve space for scroll buttons
      availableWidth -= scrollButtonWidth * 2
    }
    
    // Calculate tab width to fit all tabs
    const idealTabWidth = Math.max(16, Math.min(maxTabWidth, availableWidth / Math.max(tabs.length, 1)))
    
    // Check if we need scrolling (when tabs would be too small)
    const minUsableWidth = 20
    const shouldScroll = idealTabWidth < minUsableWidth && tabs.length > 1
    
    if (shouldScroll) {
      // Use minimum usable width and enable scrolling
      setTabWidth(minUsableWidth)
      setNeedsScrolling(true)
    } else {
      // Use calculated width and disable scrolling
      setTabWidth(Math.floor(idealTabWidth))
      setNeedsScrolling(false)
    }
  }, [tabs.length, maxTabWidth, onTabAdd, reserveAddButtonSpace])

  // Update scroll state
  const updateScrollState = useCallback(() => {
    if (!tabsContainerRef.current || !needsScrolling) return
    
    const container = tabsContainerRef.current
    const { scrollLeft, scrollWidth, clientWidth } = container
    
    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [needsScrolling])

  // Enhanced layout updates with ResizeObserver
  useEffect(() => {
    updateLayout()
    
    // Use ResizeObserver to detect container width changes
    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === containerRef.current) {
            // Debounce layout updates to avoid excessive recalculations
            setTimeout(() => {
              updateLayout()
              setTimeout(updateScrollState, 50)
            }, 10)
          }
        }
      })
      
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }
    }
    
    // Fallback window resize handler
    const handleResize = () => {
      updateLayout()
      setTimeout(updateScrollState, 100)
    }
    
    window.addEventListener('resize', handleResize)
    
    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [updateLayout, updateScrollState])

  // Scroll state updates
  useEffect(() => {
    updateScrollState()
  }, [needsScrolling, updateScrollState])

  // Scroll active tab into view
  useEffect(() => {
    if (!tabsContainerRef.current || !needsScrolling) return
    
    const activeIndex = tabs.findIndex(tab => tab.id === activeTabId)
    if (activeIndex === -1) return
    
    const container = tabsContainerRef.current
    const tabElements = container.querySelectorAll('[data-tab-id]')
    const activeTabElement = tabElements[activeIndex]
    if (!(activeTabElement instanceof HTMLElement)) return
    
    const containerRect = container.getBoundingClientRect()
    const tabRect = activeTabElement.getBoundingClientRect()
    
    // Check if tab is fully visible
    const isTabVisible = tabRect.left >= containerRect.left && tabRect.right <= containerRect.right
    
    if (!isTabVisible) {
      activeTabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }
  }, [activeTabId, tabs, needsScrolling])

  const handleScroll = useCallback(() => {
    updateScrollState()
  }, [updateScrollState])

  const scrollLeft = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({
        left: -120,
        behavior: 'smooth'
      })
    }
  }

  const scrollRight = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({
        left: 120,
        behavior: 'smooth'
      })
    }
  }

  const handleMiddleClick = async (e: React.MouseEvent, tabId: string) => {
    if (e.button === 1) {
      e.preventDefault()
      const tab = tabs.find(t => t.id === tabId)
      if (tab?.isDirty && onConfirmClose) {
        const confirmed = await onConfirmClose(tabId)
        if (confirmed) {
          onTabClose?.(tabId)
        }
      } else {
        onTabClose?.(tabId)
      }
    }
  }

  const handleMiddleClickWrapper = (e: React.MouseEvent, tabId: string) => {
    void handleMiddleClick(e, tabId)
  }

  const handleContextMenu = (e: React.MouseEvent, tabId: string) => {
    e.preventDefault()
    setContextMenu({
      tabId,
      position: { x: e.clientX, y: e.clientY }
    })
  }

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null)
  }, [])

  const createContextMenuItems = (tabId: string): ContextMenuItem[] => {
    const items: ContextMenuItem[] = []
    
    if (onTabDuplicate) {
      items.push({
        id: 'duplicate',
        label: 'Duplicate tab',
        icon: <DocumentDuplicateIcon className="w-4 h-4" />,
        onClick: () => { onTabDuplicate(tabId) }
      })
    }
    
    if (onTabClose) {
      items.push({
        id: 'close',
        label: 'Close tab',
        icon: <XMarkIcon className="w-4 h-4" />,
        onClick: () => { onTabClose(tabId) }
      })
    }
    
    if (onCloseAllTabs) {
      items.push({
        id: 'close-all',
        label: 'Close all tabs',
        icon: <XCircleIcon className="w-4 h-4" />,
        onClick: () => { onCloseAllTabs() }
      })
    }
    
    if (onCloseOtherTabs) {
      items.push({
        id: 'close-others',
        label: 'Close other tabs',
        icon: <XCircleIcon className="w-4 h-4" />,
        onClick: () => { onCloseOtherTabs(tabId) }
      })
    }
    
    if (onForceCloseAllTabs) {
      items.push({
        id: 'force-close-all',
        label: 'Force close all tabs',
        icon: <XCircleIcon className="w-4 h-4" />,
        onClick: () => { onForceCloseAllTabs() },
        dangerous: true
      })
    }
    
    return items
  }

  return (
    <div 
      ref={containerRef}
      className={`flex items-center ${containerHeightClass} bg-gray-50 dark:bg-gray-800 px-2 min-w-0 w-full ${className}`}
    >
      {/* Left scroll button */}
      {needsScrolling && (
        <button type="button"
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          className={`
            flex items-center justify-center w-8 h-8 mr-1 flex-shrink-0 rounded-md
            transition-all duration-200 ease-out
            ${canScrollLeft 
              ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700' 
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            }
          `}
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
      )}

      {/* Tabs container */}
      <div 
        ref={tabsContainerRef}
        className={`
          flex items-center flex-1 min-w-0 max-w-full h-full
          ${needsScrolling ? 'overflow-x-auto scrollbar-hide' : 'overflow-hidden'}
        `}
        onScroll={handleScroll}
      >
        {tabs.map((tab) => (
          <div key={tab.id} data-tab-id={tab.id}>
            <DraggableTab
              tab={tab}
              tabWidth={tabWidth}
              isActive={activeTabId === tab.id}
              datasourceId={datasourceId}
              databaseName={databaseName}
              schemaName={schemaName}
              onTabChange={onTabChange}
              onTabClose={onTabClose}
              onConfirmClose={onConfirmClose}
              onContextMenu={handleContextMenu}
              onMiddleClick={handleMiddleClickWrapper}
              enableDragDrop={enableDragDrop}
              tabHeight={tabHeight}
            />
          </div>
        ))}
      </div>

      {/* Right scroll button */}
      {needsScrolling && (
        <button type="button"
          onClick={scrollRight}
          disabled={!canScrollRight}
          className={`
            flex items-center justify-center w-8 h-8 ml-1 flex-shrink-0 rounded-md
            transition-all duration-200 ease-out
            ${canScrollRight 
              ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700' 
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
            }
          `}
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      )}

      {/* Add tab button - ALWAYS visible with consistent positioning */}
      {onTabAdd && (
        <button type="button"
          onClick={onTabAdd}
          className={`
            flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-md
            text-gray-600 dark:text-gray-400
            hover:text-gray-900 dark:hover:text-gray-200
            hover:bg-gray-200 dark:hover:bg-gray-600
            transition-all duration-200 ease-out
            ${needsScrolling ? 'ml-1' : 'ml-2'}
          `}
          title="New Tab"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      )}
      
      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          items={createContextMenuItems(contextMenu.tabId)}
          position={contextMenu.position}
          onClose={handleContextMenuClose}
        />
      )}
    </div>
  )
}